import Icon1 from "@assets/icons/Icon";
import { colors } from "sexy";
import { useState } from "react";
import { CustomDocument } from "@components/ui/documents/CustomDocument";
import { DocumentModal } from "@components/modals/DocumentModal";
import SearchField from "@components/ui/textfields/SearchField";
import {
  Project,
  ProjectDocument,
  ProjectFolder,
  ProjectSubFolder,
} from "types";

type FolderType = ProjectFolder | ProjectSubFolder;

const FolderItem = ({
  folder,
  depth = 0,
  onDocumentClick,
  hideHidden = false,
}: {
  folder: FolderType;
  depth?: number;
  onDocumentClick?: (documentId: string) => void;
  hideHidden?: boolean;
}) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const filteredDocs = (folder.documents || []).filter(
    (doc) => (hideHidden ? !doc.hidden : true),
  );
  const hasDocuments = filteredDocs.length > 0;
  const hasSubFolders =
    "sub_folders" in folder &&
    folder.sub_folders &&
    folder.sub_folders.length > 0;
  const indentLeft = depth * 20;

  return (
    <div className="flex flex-col">
      {/* Header with icon and title */}
      <div
        className="flex flex-row gap-2 items-center mb-1 cursor-pointer hover:opacity-70 transition-opacity"
        style={{ marginLeft: `${indentLeft}px` }}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <Icon1
          name="folder"
          size={20}
          color={
            hasDocuments || hasSubFolders
              ? colors.neutral_900
              : colors.neutral_300
          }
        />
        <span
          className="text-sm"
          style={{
            color:
              hasDocuments || hasSubFolders
                ? colors.neutral_900
                : colors.neutral_300,
          }}
        >
          {folder.name}
        </span>
      </div>

      {isExpanded && (
        <>
          {/* Documents list */}
          {hasDocuments && (
            <div className="flex flex-col mb-2">
              {filteredDocs.map((doc, docIndex) => (
                <CustomDocument
                  key={docIndex}
                  name={doc.name}
                  url={doc.file?.url || ""}
                  hidden={doc.hidden}
                  indentLeft={indentLeft + 40}
                  fontSize={14}
                  onClick={
                    onDocumentClick
                      ? () => onDocumentClick(doc.id)
                      : undefined
                  }
                />
              ))}
            </div>
          )}

          {/* Sub-folders - Recursive rendering */}
          {hasSubFolders &&
            "sub_folders" in folder &&
            folder.sub_folders?.map((subFolder, subIndex) => (
              <FolderItem
                key={subIndex}
                folder={subFolder}
                depth={depth + 1}
                onDocumentClick={onDocumentClick}
                hideHidden={hideHidden}
              />
            ))}
        </>
      )}
    </div>
  );
};

export const DocumentSection = ({
  folders = [],
  activeProject,
  setActiveProject,
  readOnly = false,
}: {
  folders: (ProjectFolder | ProjectSubFolder)[];
  activeProject?: Project;
  setActiveProject?: (project: Project) => void;
  readOnly?: boolean;
}) => {
  const [selectedDocumentId, setSelectedDocumentId] = useState<string | null>(
    null,
  );
  const [search, setSearch] = useState("");

  const normalizedSearch = search.trim().toLowerCase();

  const filterFolders = (
    items: FolderType[],
  ): FolderType[] => {
    if (!normalizedSearch) return items;
    return items
      .map((folder) => {
        const matchingDocs = (folder.documents || []).filter((doc) =>
          doc.name.toLowerCase().includes(normalizedSearch),
        );
        const matchingSubFolders =
          "sub_folders" in folder && folder.sub_folders
            ? filterFolders(folder.sub_folders)
            : [];
        if (matchingDocs.length === 0 && matchingSubFolders.length === 0)
          return null;
        return {
          ...folder,
          documents: matchingDocs,
          ...("sub_folders" in folder ? { sub_folders: matchingSubFolders } : {}),
        } as FolderType;
      })
      .filter(Boolean) as FolderType[];
  };

  const filteredFolders = filterFolders(folders);

  return (
    <div className="flex flex-col max-h-full overflow-auto">
      {selectedDocumentId && activeProject && (
        <DocumentModal
          document_id={selectedDocumentId}
          activeProject={activeProject}
          setActiveProject={setActiveProject}
          onClose={() => setSelectedDocumentId(null)}
          readOnly={readOnly}
        />
      )}

      <div className="mb-4 p-1">
        <SearchField
          value={search}
          onChange={(value) => setSearch(value)}
          placeholder="Rechercher un document..."
        />
      </div>

      {filteredFolders.map((folder, index) => (
        <FolderItem
          key={index}
          folder={folder}
          depth={0}
          hideHidden={readOnly}
          onDocumentClick={
            activeProject
              ? (docId) => setSelectedDocumentId(docId)
              : undefined
          }
        />
      ))}

      {filteredFolders.length === 0 && normalizedSearch && (
        <div className="text-h3 text-neutral-400 text-center py-4">
          Aucun document trouvé
        </div>
      )}
    </div>
  );
};

/** @deprecated Use CustomDocument directly instead */
export const DocumentItem = ({
  indentLeft,
  doc,
  onPress,
  fontSize = 14,
}: {
  indentLeft: number;
  doc: ProjectDocument;
  onPress?: () => void;
  fontSize?: number;
}) => {
  return (
    <CustomDocument
      name={doc.name}
      url={doc.file?.url || ""}
      hidden={doc.hidden}
      indentLeft={indentLeft}
      fontSize={fontSize}
      onClick={onPress}
    />
  );
};
